// Williams College: public college page (#2740), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Williams' Common Data Set 2025-2026 (Institutional Research, dated April
//    2026; the PDF on hub.williams.edu answers 403 to scripted requests, so
//    it was read through a real browser visit from the Institutional
//    Research page): C1 applicants, admits and enrollees for Fall 2025, C8
//    test policy for Fall 2027, C21/C22 with early decision counts for the
//    Fall 2025 entering class. Its C1 residency table gives totals only, so
//    no residency rows are shown. Its G1 matches the 2026-2027 comprehensive
//    fees that the Tuition & Aid page lists with their total, so the cost
//    table cites that page.
//  - williams.edu admissions: First-Year (Early Decision and Regular
//    Decision, checklist, testing) and How to Apply (the admission and
//    financial aid deadlines table, FAQs). Neither page names the entry
//    year; both were read during the fall 2027 application cycle.
//  - College Scorecard API, school id 168342 (net price by family income).
//    Three income bands are negative in the Department's own data.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Williams Common Data Set 2025-2026',
        url: 'https://hub.williams.edu/institutional-research/files/2026/04/Williams-CDS-2025-2026-V2.pdf',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Williams Admission, First-Year',
        url: 'https://www.williams.edu/admission-aid/how-to-apply/first-year/',
        read: '2026-10-01',
    },
    apply: {
        label: 'Williams Admission, How to Apply',
        url: 'https://www.williams.edu/admission-aid/how-to-apply/',
        read: '2026-10-01',
    },
    costs: {
        label: 'Williams Tuition & Aid, 2026-2027 Comprehensive Fees',
        url: 'https://www.williams.edu/admission-aid/tuition-aid/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?168342-Williams-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'williams-college',
    kbId: 'williams_college',
    name: 'Williams College',
    shortName: 'Williams',
    location: 'Williamstown, MA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Williams identity site); decoration only.
    branding: {
        primary: '#500082',
        secondary: '#FFBE0A',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/williams_college_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 15520,
        admitted: 1336,
        enrolled: 567,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Williams\' Common Data Set reports one first-year acceptance rate for the whole college, with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'Williams has one early decision round and no early action plan.',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early Decision', applied: 964, admitted: 256 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 4' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 15', decision: 'By December 14', binding: true },
            { plan: 'Regular Decision', deadline: 'January 4', decision: 'By April 1', binding: false },
        ],
        note: 'Financial aid applications are due on the same dates. Early Decision admits reply by January 4 and Regular Decision admits by May 1. Williams takes the Common App, Coalition with Scoir and QuestBridge, and does not interview. It admits students for the fall semester only.',
        source: 'apply',
        binding: 'Early Decision is binding: you sign the Early Decision Agreement Form, and if admitted you are expected to withdraw any applications to other schools.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Williams is test-optional: whether to send SAT or ACT results is up to you. If you send them, Williams superscores, and the science section is optional on the enhanced ACT. You can change your mind about sending scores at any point before your application deadline.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted, not required, for fall 2027 applicants, with scores due by January 12.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 76300 },
            { item: 'Housing', amount: 9830 },
            { item: 'Meals', amount: 9490 },
            { item: 'Activities and residential house fees', amount: 340 },
        ],
        total: 95960,
        note: 'Williams\' comprehensive fees for 2026-2027. Books, estimated at $1,000, are not in the total; Williams covers them for every student on financial aid. Students who study away also pay a $1,500 study away fee.',
        source: 'costs',
        netPrice: {
            average: 15157,
            byIncome: [
                { income: '$0 to $30,000', amount: 2291 },
                { income: '$30,001 to $48,000', amount: 3680 },
                { income: '$48,001 to $75,000', amount: 722 },
                { income: '$75,001 to $110,000', amount: 5280 },
                { income: 'Over $110,000', amount: 43912 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Williams\' aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
